import { Badge, Card, CardContent, cn } from "@safeplate/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Building2,
  BookOpenCheck,
  ClipboardList,
  Droplets,
  FileBadge2,
  Flame,
  PackageCheck,
  ShieldAlert,
  Sparkles,
  Thermometer,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";

import { PlanWorkspace } from "../plan/plan-workspace.js";
import { useShell } from "../shell/shell-context.js";
import { LocationPanel } from "./location-panel.js";
import { ResourcePanel } from "./resource-panel.js";
import {
  areaResource,
  assetResource,
  cleaningResource,
  documentResource,
  methodResource,
  reminderResource,
  riskResource,
  separationResource,
  setupCompletion,
  supplierResource,
  waterResource,
  type SetupResource,
} from "./resources.js";
import { listSetupItems } from "./setup-api.js";

type SectionKey =
  | "plan"
  | "locations"
  | "equipment"
  | "suppliers"
  | "cleaning"
  | "water"
  | "risks"
  | "methods"
  | "documents";

const sections: readonly {
  description: string;
  icon: LucideIcon;
  key: SectionKey;
  label: string;
  resources: readonly SetupResource[];
}[] = [
  {
    description: "Questionnaire and publish",
    icon: BookOpenCheck,
    key: "plan",
    label: "Food Control Plan",
    resources: [],
  },
  {
    description: "Site and council details",
    icon: Building2,
    key: "locations",
    label: "Locations",
    resources: [],
  },
  {
    description: "Areas, equipment and QR labels",
    icon: Thermometer,
    key: "equipment",
    label: "Equipment",
    resources: [areaResource, assetResource],
  },
  {
    description: "Approved delivery sources",
    icon: PackageCheck,
    key: "suppliers",
    label: "Suppliers",
    resources: [supplierResource],
  },
  {
    description: "What, when and how",
    icon: Sparkles,
    key: "cleaning",
    label: "Cleaning",
    resources: [cleaningResource],
  },
  {
    description: "Supply and treatment",
    icon: Droplets,
    key: "water",
    label: "Water",
    resources: [waterResource],
  },
  {
    description: "Nearby and separation controls",
    icon: ShieldAlert,
    key: "risks",
    label: "Risks",
    resources: [riskResource, separationResource],
  },
  {
    description: "Specialised food processes",
    icon: Flame,
    key: "methods",
    label: "Methods",
    resources: [methodResource],
  },
  {
    description: "Evidence and due dates",
    icon: FileBadge2,
    key: "documents",
    label: "Documents",
    resources: [documentResource, reminderResource],
  },
];

const completionResources = [
  areaResource,
  assetResource,
  supplierResource,
  cleaningResource,
  waterResource,
  methodResource,
];

export function SetupWorkspacePage() {
  const shell = useShell();
  const [activeSection, setActiveSection] = useState<SectionKey>("plan");
  const canManage = shell.activeRole !== "staff";
  const locationId = shell.activeLocation?.id;
  const countQueries = useQueries({
    queries: completionResources.map((resource) => ({
      enabled: Boolean(locationId && canManage),
      queryFn: () => listSetupItems(resource, locationId!),
      queryKey: ["setup", resource.key, locationId],
      staleTime: 30_000,
    })),
  });
  const counts = useMemo(
    () =>
      Object.fromEntries(
        completionResources.map((resource, index) => [
          resource.key,
          countQueries[index]?.data?.length ?? 0,
        ]),
      ),
    [countQueries],
  );
  const completion = setupCompletion(counts);
  const areas = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listSetupItems(areaResource, locationId!),
    queryKey: ["setup", "areas", locationId],
  });
  const current = sections.find((section) => section.key === activeSection)!;

  if (!canManage) {
    return (
      <>
        <header className="mb-7">
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Food Control Plan
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Location setup
          </h1>
        </header>
        <Card className="overflow-hidden">
          <CardContent className="grid min-h-72 place-items-center p-8 text-center">
            <div className="max-w-md">
              <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-stone-100 text-stone-600">
                <ClipboardList className="size-5" />
              </span>
              <h2 className="mt-4 text-xl font-black">
                Setup is managed by your manager
              </h2>
              <p className="mt-2 text-sm leading-6 text-stone-500">
                Staff can keep logging kitchen records without changing the
                location’s equipment, suppliers or food-safety plan.
              </p>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <header className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Food Control Plan
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Location setup
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Build the practical lists your team needs to log clear,
            verifier-ready evidence.
          </p>
        </div>
        {locationId ? (
          <div className="min-w-64 rounded-2xl border border-emerald-900/10 bg-emerald-800 p-4 text-white shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[0.65rem] font-bold tracking-widest text-emerald-300 uppercase">
                  Core setup
                </p>
                <p className="mt-1 text-sm font-black">{completion}% ready</p>
              </div>
              <Badge className="bg-surface/10 text-emerald-100">
                {completion === 100 ? "Complete" : "In progress"}
              </Badge>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface/15">
              <div
                className="h-full rounded-full bg-emerald-300 transition-all"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>
        ) : null}
      </header>

      <div className="grid gap-5 xl:grid-cols-[260px_minmax(0,1fr)]">
        <nav
          aria-label="Setup areas"
          className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:block xl:space-y-1"
        >
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                className={cn(
                  "flex min-h-20 w-full items-start gap-3 rounded-2xl border px-3.5 py-3 text-left transition xl:min-h-0",
                  activeSection === section.key
                    ? "border-emerald-900 bg-emerald-800 text-white shadow-sm"
                    : "border-stone-200 bg-surface text-stone-700 hover:border-stone-300 hover:bg-stone-50",
                )}
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                type="button"
              >
                <Icon
                  className={cn(
                    "mt-0.5 size-4 shrink-0",
                    activeSection === section.key
                      ? "text-emerald-300"
                      : "text-stone-400",
                  )}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-bold">
                    {section.label}
                  </span>
                  <span
                    className={cn(
                      "mt-0.5 hidden text-[0.7rem] leading-4 sm:block",
                      activeSection === section.key
                        ? "text-emerald-100/65"
                        : "text-stone-400",
                    )}
                  >
                    {section.description}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 space-y-5">
          {activeSection === "locations" ? (
            <LocationPanel canManage />
          ) : activeSection === "plan" && locationId ? (
            <PlanWorkspace canManage={canManage} locationId={locationId} />
          ) : locationId ? (
            current.resources.map((resource) => (
              <ResourcePanel
                areaOptions={(areas.data ?? []).map((area) => ({
                  label:
                    typeof area.name === "string" ? area.name : "Kitchen area",
                  value: area.id,
                }))}
                canManage
                key={resource.key}
                locationId={locationId}
                resource={resource}
              />
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <h2 className="text-lg font-black">Add a location first</h2>
                <p className="mt-2 text-sm text-stone-500">
                  Locations anchor every piece of setup and evidence.
                </p>
                <button
                  className="mt-4 text-sm font-bold text-emerald-800"
                  onClick={() => setActiveSection("locations")}
                  type="button"
                >
                  Go to locations
                </button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
